import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";

const POLL_INTERVAL = 30_000; // 30 seconds
const INIT_DELAY    = 8_000;  // wait 8s before first poll (let initial page loads finish)
const AUTO_DISMISS  = 8_000;  // notifications auto-dismiss after 8s

let _notifId = 0;
function nextId() { return ++_notifId; }

export default function PortalShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // { id, icon, color, title, body, to }
  const [notifs, setNotifs] = useState([]);

  const avisosUnread = useRef(null);   // null = not yet initialized
  const pagosMap     = useRef(null);   // { [id]: estado }

  const push = useCallback((icon, color, title, body, to) => {
    const id = nextId();
    setNotifs(prev => [...prev.slice(-2), { id, icon, color, title, body, to }]);
    setTimeout(() => {
      setNotifs(prev => prev.filter(n => n.id !== id));
    }, AUTO_DISMISS);
  }, []);

  const dismiss = useCallback((id) => {
    setNotifs(prev => prev.filter(n => n.id !== id));
  }, []);

  const poll = useCallback(async () => {
    // ── Avisos del condominio ──────────────────────────────────────────
    try {
      const r = await apiFetch("/api/core/portal/avisos/");
      const data = await r.json();
      const list = data.results ?? data;
      const unread = list.filter(a => !a.leido).length;

      if (avisosUnread.current !== null && unread > avisosUnread.current) {
        const diff = unread - avisosUnread.current;
        push(
          "fa-bell",
          "#38bdf8",
          t("portal.nuevoAvisoNotif") || "Nuevo aviso",
          diff === 1
            ? (t("portal.nuevoAvisoBody") || "Tienes un nuevo aviso del condominio.")
            : `Tienes ${diff} nuevos avisos del condominio.`,
          "/portal/avisos",
        );
      }
      avisosUnread.current = unread;
    } catch { /* ignore network errors silently */ }

    // ── Avisos de pago ────────────────────────────────────────────────
    try {
      const r = await apiFetch("/api/core/portal/avisos-pago/");
      const data = await r.json();
      const list = data.results ?? data;

      if (pagosMap.current !== null) {
        list.forEach(p => {
          const prev = pagosMap.current[p.id];
          if (prev && prev === "PENDIENTE" && p.estado !== "PENDIENTE") {
            if (p.estado === "VALIDO") {
              push(
                "fa-circle-check",
                "#4ade80",
                t("portal.pagoAprobadoNotif") || "Pago aprobado",
                t("portal.pagoAprobadoBody") || "Tu aviso de pago fue validado correctamente.",
                "/portal/avisos-pago",
              );
            } else if (p.estado === "NO_VALIDO") {
              push(
                "fa-circle-xmark",
                "#f87171",
                t("portal.pagoRechazadoNotif") || "Pago no válido",
                t("portal.pagoRechazadoBody") || "Tu aviso de pago no fue aceptado. Verifica el comprobante.",
                "/portal/avisos-pago",
              );
            }
          }
        });
      }

      const map = {};
      list.forEach(p => { map[p.id] = p.estado; });
      pagosMap.current = map;
    } catch { /* ignore */ }
  }, [push, t]);

  useEffect(() => {
    const init   = setTimeout(poll, INIT_DELAY);
    const ticker = setInterval(poll, POLL_INTERVAL);
    return () => { clearTimeout(init); clearInterval(ticker); };
  }, [poll]);

  return (
    <>
      <Outlet />

      {/* ── Notification stack (top-right fixed) ── */}
      <div style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 9999,
        display: "flex", flexDirection: "column", gap: 10,
        pointerEvents: "none",
      }}>
        {notifs.map(n => (
          <NotifToast key={n.id} notif={n} onDismiss={dismiss} onNavigate={navigate} />
        ))}
      </div>
    </>
  );
}

function NotifToast({ notif, onDismiss, onNavigate }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Tiny delay so the enter animation plays
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      onClick={() => { onDismiss(notif.id); onNavigate(notif.to); }}
      style={{
        pointerEvents: "all",
        background: "var(--bg-card)",
        border: `1px solid ${notif.color}55`,
        borderLeft: `4px solid ${notif.color}`,
        borderRadius: 12,
        padding: "14px 16px",
        width: 300,
        boxShadow: `0 8px 28px rgba(0,0,0,.28), 0 0 0 1px ${notif.color}22`,
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(20px)",
        transition: "opacity .25s ease, transform .25s ease",
        position: "relative",
      }}
    >
      {/* Icon */}
      <div style={{
        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
        background: `${notif.color}18`,
        border: `1px solid ${notif.color}44`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${notif.icon}`} style={{ color: notif.color, fontSize: 15 }} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.3 }}>
          {notif.title}
        </p>
        <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.45 }}>
          {notif.body}
        </p>
        <span style={{ fontSize: 11, color: notif.color, fontWeight: 700, marginTop: 4, display: "inline-block" }}>
          Toca para ver →
        </span>
      </div>

      {/* Dismiss ✕ */}
      <button
        onClick={e => { e.stopPropagation(); onDismiss(notif.id); }}
        style={{
          position: "absolute", top: 8, right: 8,
          background: "none", border: "none", cursor: "pointer",
          color: "var(--text-secondary)", fontSize: 13, lineHeight: 1,
          padding: 4, borderRadius: 5,
        }}
      >
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  );
}
